import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { usePerSourceAccuracy } from "../hooks/useOcrHealthData";
import { SOURCE_COLORS } from "../sourceColors";
import { PanelCard, PanelError, PanelStatus } from "./PanelCard";

interface Pivoted {
  day: string;
  [source: string]: string | number;
}

export function PerSourceAccuracyTrend() {
  const { data, isLoading, error } = usePerSourceAccuracy(30);

  if (isLoading) return <PanelCard title="Per-Source Accuracy (last 30d)"><PanelStatus>Loading…</PanelStatus></PanelCard>;
  if (error) return <PanelCard title="Per-Source Accuracy (last 30d)"><PanelError>Couldn't load — {error}</PanelError></PanelCard>;
  if (!data || data.series.length === 0) {
    return (
      <PanelCard title="Per-Source Accuracy (last 30d)">
        <PanelStatus>No verified extractions yet</PanelStatus>
      </PanelCard>
    );
  }

  // Pivot: one row per day with one column per source.
  const dayMap = new Map<string, Pivoted>();
  for (const series of data.series) {
    for (const point of series.points) {
      const existing = dayMap.get(point.day) ?? { day: point.day };
      existing[series.source] = point.accuracy;
      dayMap.set(point.day, existing);
    }
  }
  const chartData = Array.from(dayMap.values()).sort((a, b) => a.day.localeCompare(b.day));

  return (
    <PanelCard title="Per-Source Accuracy (last 30d)">
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="day" fontSize={11} />
          <YAxis
            domain={[0, 1]}
            tickFormatter={(v: number) => `${Math.round(v * 100)}%`}
            fontSize={11}
          />
          <Tooltip formatter={(v: number) => `${(v * 100).toFixed(1)}%`} />
          <Legend />
          {data.series.map((s) => (
            <Line
              key={s.source}
              type="monotone"
              dataKey={s.source}
              stroke={SOURCE_COLORS[s.source] ?? "#999"}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </PanelCard>
  );
}
